import { useForm } from '@tanstack/react-form';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import type { GetRequest } from '@cold-ambulance/client';
import {
  AMBULANCE_SERVICE_TYPES,
  calculateTotal,
  formatCurrency,
} from '@cold-ambulance/isomorphic';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Label,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  PriceField,
  ReviewRequestDetails,
  ReviewRequestDetailsSkeleton,
  errorToFormIssue,
  useAction,
  useData,
} from '@cold-ambulance/ui';

type PriceItem = {
  serviceNameKey: string;
  serviceNameParams?: Record<string, string>;
  equipmentInfo: string;
  serviceType: string;
  count: number;
  unitPrice: number;
};

const steps = {
  '': { step: 1, titleKey: 'requests.requestDetails' },
  '#step-2': { step: 2, titleKey: 'offers.submitOffer' },
} as const;

type StepHash = keyof typeof steps;

export default function PlaceOfferPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { requestId } = useParams();

  const currentStep = steps[location.hash as StepHash] ?? steps[''];

  const requestQuery = useData(
    'GET /requests/{id}',
    { id: requestId! },
    { enabled: !!requestId },
  );

  return (
    <div className="page-container">
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">{t(currentStep.titleKey)}</h1>
          <Link
            to="/offers"
            viewTransition
            className="text-primary flex gap-x-1 text-sm font-bold"
          >
            <span>{t('common.back')}</span>
            <ArrowLeft />
          </Link>
        </div>
        <div className="mt-8">
          {requestQuery.isLoading || !requestQuery.data ? (
            <ReviewRequestDetailsSkeleton />
          ) : currentStep.step === 1 ? (
            <>
              <ReviewRequestDetails request={requestQuery.data} />
              <Link
                to="#step-2"
                className="bg-primary text-primary-foreground mt-6 flex w-full items-center justify-center rounded-md py-2 font-medium"
              >
                {t('common.next')}
              </Link>
            </>
          ) : (
            <PriceTable request={requestQuery.data} />
          )}
        </div>
      </div>
    </div>
  );
}

function PriceTable({ request }: { request: GetRequest }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const placeOffer = useAction('POST /offers', {
    invalidate: ['GET /requests'],
  });

  const initialPriceItems = buildPriceItems(request);

  const requiresBankGuarantee = (request.bankGuaranteePercent ?? 0) > 0;

  const form = useForm({
    defaultValues: {
      priceItems: initialPriceItems,
      notes: '',
      bankGuaranteeConfirmed: false,
    },
    validators: {
      onSubmitAsync: async (props) => {
        const invalidPriceItem = props.value.priceItems.find(
          (item) => item.unitPrice <= 0,
        );
        if (invalidPriceItem) {
          return {
            form: [{ message: 'يجب إدخال سعر صحيح لجميع الخدمات' }],
          };
        }

        if (requiresBankGuarantee && !props.value.bankGuaranteeConfirmed) {
          return {
            form: [{ message: 'يجب الموافقة على شروط الضمان البنكي للمتابعة' }],
          };
        }
        try {
          await placeOffer.mutateAsync({
            requestId: request.id,
            priceItems: props.value.priceItems.map((item) => ({
              serviceName: t(item.serviceNameKey, item.serviceNameParams),
              serviceType: item.serviceType,
              count: item.count,
              unitPrice: item.unitPrice,
            })),
            notes: props.value.notes || undefined,
          });
          navigate('/offers/success');
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <Card className="border-primary bg-white py-5">
      <CardContent>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">
                    {t('offers.service')}
                  </TableHead>
                  <TableHead className="text-center">
                    {t('offers.quantity')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('offers.unitPrice')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('offers.total')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <form.Field name="priceItems" mode="array">
                  {(field) =>
                    field.state.value.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {t(item.serviceNameKey, item.serviceNameParams)}
                          {item.equipmentInfo}
                        </TableCell>
                        <TableCell className="text-center">
                          {item.count}
                        </TableCell>
                        <TableCell>
                          <form.Field
                            name={`priceItems[${index}].unitPrice`}
                            validators={{
                              onBlur: z.number().positive({
                                message: 'السعر يجب أن يكون أكبر من صفر',
                              }),
                            }}
                          >
                            {(priceField) => (
                              <div className="flex flex-col gap-1">
                                <PriceField
                                  label=""
                                  value={priceField.state.value as number}
                                  onBlur={priceField.handleBlur}
                                  onChange={priceField.handleChange}
                                />
                                <FieldMessage
                                  errors={priceField.state.meta.errors}
                                />
                              </div>
                            )}
                          </form.Field>
                        </TableCell>
                        <TableCell>
                          <form.Subscribe
                            selector={(s) =>
                              s.values.priceItems[index]?.unitPrice ?? 0
                            }
                          >
                            {(unitPrice) => (
                              <span>
                                {formatCurrency(item.count * unitPrice)}
                              </span>
                            )}
                          </form.Subscribe>
                        </TableCell>
                      </TableRow>
                    ))
                  }
                </form.Field>
              </TableBody>
              <form.Subscribe
                selector={(state) => state.values.priceItems}
                children={(priceItems) => {
                  const { total, workingDays, workingHoursPerDay } =
                    calculateTotal({
                      priceItems,
                      scheduleStartDate: request.scheduleStartDate,
                      scheduleEndDate: request.scheduleEndDate,
                      scheduleHoursFrom: request.scheduleHoursFrom,
                      scheduleHoursTo: request.scheduleHoursTo,
                    });

                  return (
                    <TableFooter>
                      <TableRow>
                        <TableCell colSpan={3} className="font-medium">
                          {t('offers.grandTotal', {
                            days: workingDays,
                            hours: workingHoursPerDay,
                          })}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 font-bold">
                            <span>{formatCurrency(total)}</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableFooter>
                  );
                }}
              />
            </Table>
          </div>

          <p className="text-muted-foreground mt-4 text-sm">
            {t('offers.platformFees')}
          </p>

          <form.Field
            name="notes"
            children={(field) => (
              <div className="mt-6 grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="notes">
                  {t('offers.additionalNotes')}
                  <span className="text-muted-foreground">
                    {t('offers.optional')}
                  </span>
                </Label>
                <Textarea
                  id="notes"
                  className="min-h-20"
                  maxLength={1000}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <div className="flex items-center justify-between">
                  <FieldMessage errors={field.state.meta.errors} />
                  <span className="text-muted-foreground text-sm">
                    {t('offers.charCount', { count: field.state.value.length })}
                  </span>
                </div>
              </div>
            )}
          />

          {requiresBankGuarantee && (
            <form.Field
              name="bankGuaranteeConfirmed"
              children={(field) => (
                <div className="mt-6 flex items-center gap-3">
                  <Checkbox
                    id="bankGuaranteeConfirmed"
                    checked={field.state.value}
                    onCheckedChange={(checked) =>
                      field.handleChange(checked === true)
                    }
                  />
                  <Label
                    htmlFor="bankGuaranteeConfirmed"
                    className="text-sm leading-relaxed"
                  >
                    {t('offers.bankGuaranteeAck', {
                      percent: request.bankGuaranteePercent,
                    })}
                  </Label>
                </div>
              )}
            />
          )}

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <Button
                type="submit"
                disabled={!canSubmit}
                className="mt-6 w-full"
              >
                {isSubmitting
                  ? t('offers.submittingOffer')
                  : t('offers.submitOfferBtn')}
              </Button>
            )}
          />
        </form>
      </CardContent>
    </Card>
  );
}

type EquipmentItem = { name: string; quantity: number };

function formatEquipmentInfo(equipment: EquipmentItem[]): string {
  if (!equipment || equipment.length === 0) return '';
  return ` (${equipment.map((e) => `${e.name} × ${e.quantity}`).join('، ')})`;
}

function buildPriceItems(request: GetRequest): PriceItem[] {
  const items: PriceItem[] = [];

  for (const service of request.requestParamedicService) {
    if (service.amount > 0) {
      items.push({
        serviceNameKey:
          service.type === 'SPECIALIST'
            ? 'services.paramedicSpecialist'
            : 'services.paramedicTechnician',
        equipmentInfo: '',
        serviceType: `paramedic_${service.type.toLowerCase()}`,
        count: service.amount,
        unitPrice: 0,
      });
    }
  }

  for (const service of request.requestNurseService) {
    if (service.amount > 0) {
      items.push({
        serviceNameKey:
          service.type === 'SPECIALIST'
            ? 'services.nurseSpecialist'
            : 'services.nurseTechnician',
        equipmentInfo: '',
        serviceType: `nurse_${service.type.toLowerCase()}`,
        count: service.amount,
        unitPrice: 0,
      });
    }
  }

  for (const service of request.doctorService) {
    if (service.amount > 0) {
      items.push({
        serviceNameKey: 'services.doctorWithSpec',
        serviceNameParams: { specialization: service.specialization },
        equipmentInfo: '',
        serviceType: `doctor_${service.specialization.toLowerCase().replace(/\s+/g, '_')}`,
        count: service.amount,
        unitPrice: 0,
      });
    }
  }

  for (const service of request.ambulanceServiceWithDriver) {
    if (service.type1Amount > 0) {
      items.push({
        serviceNameKey: 'services.ambulanceWithDriver',
        serviceNameParams: { type: AMBULANCE_SERVICE_TYPES.type1.name },
        equipmentInfo: formatEquipmentInfo(
          service.type1Equipment as EquipmentItem[],
        ),
        serviceType: 'ambulance_with_driver_type1',
        count: service.type1Amount,
        unitPrice: 0,
      });
    }
    if (service.type2Amount > 0) {
      items.push({
        serviceNameKey: 'services.ambulanceWithDriver',
        serviceNameParams: { type: AMBULANCE_SERVICE_TYPES.type2.name },
        equipmentInfo: formatEquipmentInfo(
          service.type2Equipment as EquipmentItem[],
        ),
        serviceType: 'ambulance_with_driver_type2',
        count: service.type2Amount,
        unitPrice: 0,
      });
    }
    if (service.type3Amount > 0) {
      items.push({
        serviceNameKey: 'services.ambulanceWithDriver',
        serviceNameParams: { type: AMBULANCE_SERVICE_TYPES.type3.name },
        equipmentInfo: formatEquipmentInfo(
          service.type3Equipment as EquipmentItem[],
        ),
        serviceType: 'ambulance_with_driver_type3',
        count: service.type3Amount,
        unitPrice: 0,
      });
    }
  }

  for (const service of request.ambulanceServiceWithoutDriver) {
    if (service.type1Amount > 0) {
      items.push({
        serviceNameKey: 'services.ambulanceWithoutDriver',
        serviceNameParams: { type: AMBULANCE_SERVICE_TYPES.type1.name },
        equipmentInfo: formatEquipmentInfo(
          service.type1Equipment as EquipmentItem[],
        ),
        serviceType: 'ambulance_without_driver_type1',
        count: service.type1Amount,
        unitPrice: 0,
      });
    }
    if (service.type2Amount > 0) {
      items.push({
        serviceNameKey: 'services.ambulanceWithoutDriver',
        serviceNameParams: { type: AMBULANCE_SERVICE_TYPES.type2.name },
        equipmentInfo: formatEquipmentInfo(
          service.type2Equipment as EquipmentItem[],
        ),
        serviceType: 'ambulance_without_driver_type2',
        count: service.type2Amount,
        unitPrice: 0,
      });
    }
    if (service.type3Amount > 0) {
      items.push({
        serviceNameKey: 'services.ambulanceWithoutDriver',
        serviceNameParams: { type: AMBULANCE_SERVICE_TYPES.type3.name },
        equipmentInfo: formatEquipmentInfo(
          service.type3Equipment as EquipmentItem[],
        ),
        serviceType: 'ambulance_without_driver_type3',
        count: service.type3Amount,
        unitPrice: 0,
      });
    }
  }

  for (const service of request.ambulance4x4WithDriver) {
    if (service.amount > 0) {
      items.push({
        serviceNameKey: 'services.ambulance4x4WithDriver',
        equipmentInfo: formatEquipmentInfo(
          (service.equipment || []) as EquipmentItem[],
        ),
        serviceType: 'ambulance_4x4_with_driver',
        count: service.amount,
        unitPrice: 0,
      });
    }
  }

  for (const service of request.ambulance4x4WithoutDriver) {
    if (service.amount > 0) {
      items.push({
        serviceNameKey: 'services.ambulance4x4WithoutDriver',
        equipmentInfo: formatEquipmentInfo(
          (service.equipment || []) as EquipmentItem[],
        ),
        serviceType: 'ambulance_4x4_without_driver',
        count: service.amount,
        unitPrice: 0,
      });
    }
  }

  for (const service of request.mobileClinicService) {
    if (service.amount > 0) {
      const advanced = service.type === 'ADVANCED';
      items.push({
        serviceNameKey: advanced
          ? 'services.mobileClinicAdvanced'
          : 'services.mobileClinicBasic',
        equipmentInfo: formatEquipmentInfo(
          (service.equipment || []) as EquipmentItem[],
        ),
        serviceType: advanced ? 'mobile_clinic_advanced' : 'mobile_clinic_basic',
        count: service.amount,
        unitPrice: 0,
      });
    }
  }

  return items;
}
